import React from 'react';
import { useTranslation } from 'react-i18next';
import { useParams, useNavigate } from 'react-router-dom';

import Box from '@mui/material/Box';
import Grid from '@mui/material/Grid';
import Paper from '@mui/material/Paper';
import Stack from '@mui/material/Stack';
import Button from '@mui/material/Button';
import Popover from '@mui/material/Popover';
import Container from '@mui/material/Container';
import Typography from '@mui/material/Typography';
import CircularProgress from '@mui/material/CircularProgress';

import Iconify from '../../theme/iconify/iconify';
import { useResponsive } from '../../hooks/use-responsive';

import TOC from './TOC';
import Chat from './chat/Chat';
import { SmTabs } from '../util/Tabs';
import Content from './content/Content';
import QuizModal from './modal/quiz/QuizModal';
import ContentModal from './modal/content/Content';
import SectionModal from './modal/content/Section';
import Material from './material/Material';
import Participants from './participants/Participants';
import Settings from './settings/Settings';
import { mislecAPI } from '../../util/axios';
import SectionType from './modal/content/SectionType';
import Notification from '../util/Notification';
import SubsectionModal from './modal/content/Subsection';
import SubsectionType from './modal/content/SubsectionType';
import ConfirmationModal from '../util/ConfirmationModal';
import { MaterialContext } from '../../context/MaterialContext';
import Analytics from "./analytics/Analytics";
import { alpha } from "@mui/material/styles";

function Course({ user, updateUser }) {
    //  Course _id
    const params = useParams();
    const _id = params.id;
    const _tab = params.tab ? params.tab : 0;
    const _section = params.section ? params.section : null;
    const TABS = ['', 'material', 'participants', 'analytics', 'settings'];

    const [tab, setTab] = React.useState(0);
    const [course, setCourse] = React.useState(null);
    const [activeSection, setActiveSection] = React.useState(0);
    const [contentModal, setContentModal] = React.useState(false);
    const [sectionTypeModal, setSectionTypeModal] = React.useState(false);
    const [subsectionTypeModal, setSubsectionTypeModal] = React.useState(false);
    const [sectionModal, setSectionModal] = React.useState(false);
    const [subsectionModal, setSubsectionModal] = React.useState(false);
    const [subsectionTitle, setSubsectionTitle] = React.useState('');
    const [subsectionContent, setSubsectionContent] = React.useState('');
    const [editingSection, setEditingSection] = React.useState(false);
    const [sectionTitle, setSectionTitle] = React.useState('');
    const [editingSubsection, setEditingSubsection] = React.useState(false);
    const [editSubsectionIndex, setEditSubsectionIndex] = React.useState(null);
    const [quiz, setQuiz] = React.useState(null);
    const [quizModal, setQuizModal] = React.useState(false);
    const [finalExam, setFinalExam] = React.useState(false);
    const [removeSectionModal, setRemoveSectionModal] = React.useState(false);
    const [removeSubsectionModal, setRemoveSubsectionModal] = React.useState(false);
    const [removeQuizModal, setRemoveQuizModal] = React.useState(false);
    const [removeCourseModal, setRemoveCourseModal] = React.useState(false);
    const { setMaterial } = React.useContext(MaterialContext);
    const [removeMaterialId, setRemoveMaterialId] = React.useState(null);
    const [removeMaterialModal, setRemoveMaterialModal] = React.useState(false);
    const [revokeAccessModal, setRevokeAccessModal] = React.useState(false);
    const [revokeAccessId, setRevokeAccessId] = React.useState(null);
    const [toc, setTOC] = React.useState('0');
    const [chat, setChat] = React.useState(null);
    const [findOutMorePrompt, setFindOutMorePrompt] = React.useState(null);
    const [open, setOpen] = React.useState(false);
    const [message, setMessage] = React.useState(null);
    const [severity, setSeverity] = React.useState(null);

    const { t } = useTranslation();
    const navigate = useNavigate();
    const mdUp = useResponsive('up', 'md');

    //  Tab configuration
    const configStudent = [
        {
            label: t('course.tabs.course.title')
        },
        {
            label: t('course.tabs.material.title')
        }
    ];
    const configProfessor = [
        ...configStudent,
        {
            label: t('course.tabs.participants.title')
        },
        {
            label: t('course.tabs.analytics.title')
        },
        {
            label: t('course.tabs.settings.title')
        }
    ];

    React.useEffect(() => {
        setActiveSection(0);
        setTOC('0');
        setCourse(null);
        setMaterial(null);

        setTimeout(() => {
            updateTab();
        }, 200);
    }, [_id]);

    React.useEffect(() => {
        updateTab();
    }, [_tab]);

    React.useEffect(() => {
        if (!course) return;
        if (_tab === 'section' && _section) {
            const sectionIndex = course?.sections?.findIndex(section => section.id === _section);
            if (sectionIndex !== -1) {
                setActiveSection(sectionIndex);
                setTOC(`${sectionIndex}`);
            }
        }
    }, [course, _section]);

    React.useEffect(() => {
        if (!course || !('_id' in course) || !user) return;

        const canManageCourse = user.role === 'admin' || user._id === course.user;
        if (!canManageCourse && tab > 1) {
            setTab(0);
            navigate(`/course/${_id}`);
        }
    }, [course, user, tab, _id, navigate]);

    const updateTab = () => {
        setTab(TABS.indexOf(_tab) !== -1 ? TABS.indexOf(_tab) : 0);
    }

    const fetchCourse = async () => {
        if (!user) return;

        //  Fetch course from the server
        mislecAPI.get(`/api/course/${_id}`)
            .then(async (res) => {
                setCourse(res.data.course);

                if (user.courses.find((c) => c.id === _id)?.quiz?.some((q) => q.is_solving)) {
                    //  Redirect to unfinished exam
                    setActiveSection(res.data.course.sections.findIndex(section => section.subsections.some(subsection => subsection?.id === user.courses.find((c) => c.id === _id)?.quiz?.find((q) => q.is_solving)?.id)));
                    setTOC(`${res.data.course.sections.findIndex(section => section.subsections.some(subsection => subsection?.id === user.courses.find((c) => c.id === _id)?.quiz?.find((q) => q.is_solving)?.id))}`);
                }
            })
            .catch(error => {
                setCourse({});

                //  Notify user of error
                setMessage(error.response ? t(error.response.data.detail) : t('api.general.500'));
                setSeverity('error');
                setOpen(true);

                //  Expired session
                if (error.response)
                    if (error.response.data.detail === 'api.auth.verify.401')
                        updateUser(null);
            });
    }

    const saveCourse = (payload) => {
        if (!user) return;

        if (payload.title === '') {
            setOpen(true);
            setMessage(t('course.error.course.title'));
            setSeverity('warning');
            return;
        }

        //  Data preparation
        const data = course;
        if (payload.title) data.title = payload.title;
        if (payload.language) data.language = payload.language;
        if (payload.tags) data.tags = payload.tags;
        if (payload.published !== undefined) data.published = payload.published;
        if (payload.order) data.sections = payload.order.map((id) => data.sections.find((section) => section.id === id));

        update(data);
    }

    const sectionVisibility = (index) => {
        if (!user) return;

        //  Data preparation
        const data = course;
        if (index !== undefined) data.sections[index].visible = !data.sections[index].visible;
        else data.sections[activeSection].visible = !data.sections[activeSection].visible;
        update(data);
    }

    const saveSection = () => {
        if (!user) return;

        if (sectionTitle === '') {
            setOpen(true);
            setMessage(t('course.error.section.title'));
            setSeverity('warning');
            return;
        }

        //  Data preparation
        const data = course;
        if (!editingSection) {
            data.sections.push({
                title: sectionTitle,
                subsections: [],
                visible: false
            });
        } else {
            data.sections[activeSection].title = sectionTitle;
        }

        update(data);
        if (!editingSection) {
            setActiveSection(data.sections.length - 1);
            setTOC(`${data.sections.length - 1}`);
        }
    }

    const saveSubsection = (contentOverride) => {
        if (!user) return;

        if (subsectionTitle === '') {
            setOpen(true);
            setMessage(t('course.error.subsection.title'));
            setSeverity('warning');
            return;
        }

        const effectiveContent = (contentOverride ?? subsectionContent);
        if (effectiveContent === '' || effectiveContent === null) {
            setOpen(true);
            setMessage(t('course.error.subsection.content'));
            setSeverity('warning');
            return;
        }

        //  Data preparation
        const data = course;
        if (!editingSubsection) {
            data.sections[activeSection].subsections.push({
                title: subsectionTitle,
                content: effectiveContent
            });
        } else {
            data.sections[activeSection].subsections[editSubsectionIndex] = {
                title: subsectionTitle,
                content: effectiveContent
            };
        }

        update(data);
    }

    const saveQuiz = (quiz, title) => {
        if (!user) return;

        //  Data preparation
        const data = course;
        let newExam = false;

        if (quiz.type === 'exam') {
            if (quiz.id) {
                data.sections[activeSection].title = title;
                data.sections[activeSection].subsections.filter(subsection => subsection.id === quiz.id)[0].quiz = quiz;
            } else {
                newExam = true;
                data.sections.push({
                    title: title,
                    visible: false,
                    subsections: [{
                        type: 'QUIZ',
                        quiz: quiz
                    }]
                });
            }
        } else {
            if (editSubsectionIndex) {
                data.sections[activeSection].subsections[editSubsectionIndex].quiz = quiz;
                setEditSubsectionIndex(null);
            } else {
                data.sections[activeSection].subsections.push({
                    type: 'QUIZ',
                    quiz: quiz
                });
            }
        }

        update(data);
        if (newExam) {
            setActiveSection(data.sections.length - 1);
            setTOC(`${data.sections.length - 1}`);
        }
    }

    const resetQuiz = (quiz, subsection) => {
        //  Data  preparation
        const data = {
            course_id: _id,
            quiz: {
                id: subsection,
                feedback: null,
                questions: quiz.questions.map(q => ({
                    id: q.id,
                    answers: []
                }))
            }
        };

        //  Send data to the server
        mislecAPI.put(`/api/users/${user._id}/quiz`, data)
            .then((res) => updateUser(res.data.user))
            .catch((error) => {
                //  Expired session
                if (error.response)
                    if (error.response.data.detail === 'api.auth.verify.401')
                        updateUser(null);
            });
    }

    const removeSection = () => {
        if (!user) return;

        const data = course;
        data.sections.splice(activeSection, 1);

        if (activeSection === data.sections.length) {
            setActiveSection(activeSection - 1);
            setTOC(`${activeSection - 1}`);
        }

        update(data);
    }

    const removeSubsection = () => {
        if (!user) return;

        const data = course;
        data.sections[activeSection].subsections.splice(editSubsectionIndex, 1);

        update(data);
    }

    const removeMaterial = () => {
        if (!user) return;

        mislecAPI.delete(`/api/course_document/${removeMaterialId}`)
            .then(res => {
                //  Notify user of successful removal
                setMessage(t(res.data.detail));
                setSeverity('success');
                setOpen(true);

                //  Refresh material
                setMaterial((prev) => prev.filter(doc => doc._id !== removeMaterialId));

                //  Close modal
                setRemoveMaterialModal(false);
                setRemoveMaterialId(null);
            })
            .catch(error => {
                //  Notify user of error
                setMessage(error.response ? t(error.response.data.detail) : t('api.general.500'));
                setSeverity('error');
                setOpen(true);

                //  Expired session
                if (error.response)
                    if (error.response.data.detail === 'api.auth.verify.401')
                        updateUser(null);
            });
    }

    const revokeAccess = () => {
        if (!user) return;

        const data = {
            user_id: revokeAccessId,
            status: false
        };

        //  Send data to server
        mislecAPI.post(`/api/course/enroll/${_id}`, data)
            .then(res => {
                //  Notify user of successful removal
                setMessage(t(res.data.detail));
                setSeverity('success');
                setOpen(true);

                //  Close modal
                setRevokeAccessModal(false);
                setRevokeAccessId(null);
            })
            .catch(error => {
                //  Notify user of error
                setMessage(error.response ? t(error.response.data.detail) : t('api.general.500'));
                setSeverity('error');
                setOpen(true);

                //  Expired session
                if (error.response)
                    if (error.response.data.detail === 'api.auth.verify.401')
                        updateUser(null);
            });
    }

    const update = (data) => {
        //  Send data to server
        mislecAPI.put(`/api/course/${_id}`, data)
            .then(res => {
                //  Fetch corpuses
                fetchCourse();

                //  Notify user of success
                setOpen(true);
                setMessage(t(res.data.detail));
                setSeverity('success');

                //  Reset forms and modals
                setSectionTitle('');
                setEditingSection(false);
                setSubsectionTitle('');
                setSubsectionContent('');
                setEditingSubsection(false);
                setEditSubsectionIndex(null);
                setSectionModal(false);
                setQuiz(null);
                setQuizModal(false);
                setSubsectionModal(false);
                setRemoveSectionModal(false);
                setRemoveSubsectionModal(false);
                setRemoveQuizModal(false);
            })
            .catch(error => {
                //  Notify user of error
                setMessage(error.response ? t(error.response.data.detail) : t('api.general.500'));
                setSeverity('error');
                setOpen(true);

                //  Expired session
                if (error.response)
                    if (error.response.data.detail === 'api.auth.verify.401')
                        updateUser(null);
            });
    }

    const remove = () => {
        //  Remove course from the server
        mislecAPI.delete(`/api/course/${_id}`)
            .then(res => {
                //  Notify user of successful removal
                setMessage(t(res.data.detail));
                setSeverity('success');
                setOpen(true);
                //  Close modal
                setRemoveCourseModal(false);

                //  Redirect to corpus page
                setTimeout(() => {
                    navigate('/course');
                    updateUser(null)
                }, 2500);
            })
            .catch(error => {
                //  Notify user of error
                setMessage(error.response ? t(error.response.data.detail) : t('api.general.500'));
                setSeverity('error');
                setOpen(true);

                //  Expired session
                if (error.response)
                    if (error.response.data.detail === 'api.auth.verify.401')
                        updateUser(null);
            });
    }

    const handleTOC = (event, toc) => {
        const [section, subsection] = toc.split('_');

        if (!subsection) setTimeout(() => { window.scrollTo({ top: 0, behavior: 'smooth' }); }, 100);

        setTab(0);
        navigate(`/course/${_id}`);
        setTOC(toc);
        setActiveSection(parseInt(section));

        setTimeout(() => {
            document.getElementById(`mISLec_subsection_${subsection}`)?.scrollIntoView({
                behavior: 'smooth',
                block: 'start',
            });
        }, 100);
    }

    const handleTabChange = (event, newValue) => {
        if (user.courses.find((c) => c.id === _id)?.quiz?.some((q) => q.is_solving)) return;
        setTab(newValue);
        if (newValue < TABS.length) navigate(`/course/${_id}${newValue === 0 ? '' : `/${TABS[newValue]}`}`);
    }

    const handleQuiz = (modal, final) => {
        setQuizModal(modal);
        setFinalExam(final);
    }

    React.useEffect(() => {
        if (!quizModal) setFinalExam(false);
    }, [quizModal]);

    React.useEffect(() => {
        fetchCourse();
    }, [user, _id]);

    React.useEffect(() => {
        if (!subsectionModal) {
            setSubsectionTitle('');
            setSubsectionContent('');
            setEditingSubsection(false);
            setEditSubsectionIndex(null);
        }
    }, [subsectionModal]);

    React.useEffect(() => {
        if (!quizModal) setEditSubsectionIndex(null);
    }, [quizModal]);

    React.useEffect(() => {
        if (!course) fetchCourse();
    }, [course]);

    return (
        <>
            <Container>
                <Grid container>
                    <Grid item md={10} xs={12} sx={{ paddingRight: { md: '8px', xs: 0 } }}>
                        <Paper elevation={2} sx={{ mr: { md: 2, xs: 0 } }}>
                            <Box
                                sx={{
                                    p: {
                                        md: 4,
                                        xs: 2
                                    },
                                    backgroundColor: 'white',
                                    borderRadius: '16px'
                                }}
                            >
                                {course && ('_id' in course) &&
                                    <>
                                        <Stack direction='row' alignItems='center' justifyContent='space-between' mb={0} p={1}>
                                            <Typography variant='h4'>{course.title}</Typography>
                                        </Stack>
                                        <Grid container className='mISLec_course_tabs'>
                                            <SmTabs
                                                config={user.role === 'admin' || user._id === course.user ? configProfessor : configStudent}
                                                tab={tab}
                                                handleTabChange={handleTabChange}
                                                sx={{ mb: 2, p: 1 }}
                                            />
                                        </Grid>
                                        {tab === 0 &&
                                            <Content
                                                course={course}
                                                activeSection={activeSection}
                                                setActiveSection={setActiveSection}
                                                update={update}
                                                saveSection={saveSection}
                                                editingSection={editingSection}
                                                setEditingSection={setEditingSection}
                                                setEditingSubsection={setEditingSubsection}
                                                setEditSubsectionIndex={setEditSubsectionIndex}
                                                sectionTitle={sectionTitle}
                                                setSectionTitle={setSectionTitle}
                                                sectionVisibility={sectionVisibility}
                                                setSubsectionTitle={setSubsectionTitle}
                                                setSubsectionContent={setSubsectionContent}
                                                setQuiz={setQuiz}
                                                resetQuiz={resetQuiz}
                                                setFindOutMorePrompt={setFindOutMorePrompt}
                                                setTOC={setTOC}
                                                setQuizModal={handleQuiz}
                                                setContentModal={setContentModal}
                                                setSubsectionModal={setSubsectionModal}
                                                setRemoveQuizModal={setRemoveQuizModal}
                                                setRemoveSectionModal={setRemoveSectionModal}
                                                setRemoveSubsectionModal={setRemoveSubsectionModal}
                                                user={user}
                                                updateUser={updateUser}
                                            />
                                        }
                                        {tab === 1 &&
                                            <Material
                                                course={course}
                                                user={user}
                                                updateUser={updateUser}
                                                setOpen={setOpen}
                                                setMessage={setMessage}
                                                setSeverity={setSeverity}
                                                setRemoveMaterialId={setRemoveMaterialId}
                                                setRemoveMaterialModal={setRemoveMaterialModal}
                                            />
                                        }
                                        {tab === 2 && (user.role === 'admin' || user._id === course.user) &&
                                            <Participants
                                                course={course}
                                                setOpen={setOpen}
                                                setMessage={setMessage}
                                                setSeverity={setSeverity}
                                                updateUser={updateUser}
                                                setRevokeAccessId={setRevokeAccessId}
                                                revokeAccessModal={revokeAccessModal}
                                                setRevokeAccessModal={setRevokeAccessModal}
                                            />
                                        }
                                        {tab === 3 && (user.role === 'admin' || user._id === course.user) &&
                                            <Analytics
                                                course={course}
                                                user={user}
                                                updateUser={updateUser}
                                            />
                                        }
                                        {tab === 4 && (user.role === 'admin' || user._id === course.user) &&
                                            <Settings
                                                course={course}
                                                saveCourse={saveCourse}
                                                sectionVisibility={sectionVisibility}
                                                setRemoveCourseModal={setRemoveCourseModal}
                                                setOpen={setOpen}
                                                setMessage={setMessage}
                                                setSeverity={setSeverity}
                                                user={user}
                                                updateUser={updateUser}
                                            />
                                        }
                                    </>
                                }
                                {!course &&
                                    <Grid item xs={12}>
                                        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: '700px' }}>
                                            <CircularProgress />
                                        </Box>
                                    </Grid>
                                }
                                {course && !('_id' in course) &&
                                    <Grid item xs={12} md={12} variant='body2'>
                                        <Box>
                                            <Box
                                                sx={{
                                                    py: 4,
                                                    maxWidth: 480,
                                                    mx: 'auto',
                                                    display: 'flex',
                                                    textAlign: 'center',
                                                    alignItems: 'center',
                                                    flexDirection: 'column',
                                                    justifyContent: 'center',
                                                }}
                                            >
                                                <Typography variant='h3' sx={{ mb: 3 }}>
                                                    {t('course.error.course.empty.title')}
                                                </Typography>

                                                <Typography sx={{ color: 'text.secondary' }}>
                                                    {t('course.error.course.empty.description')}
                                                </Typography>

                                                <Box
                                                    component='img'
                                                    src='/assets/images/DR-mISLec-404.png'
                                                    sx={{
                                                        mx: 'auto',
                                                        height: 'auto',
                                                        my: { xs: 5, sm: 10 },
                                                    }}
                                                />

                                                <Button
                                                    variant=''
                                                    startIcon={<Iconify icon='mdi:book-outline' />}
                                                    onClick={() => navigate('/course')}
                                                    sx={{
                                                        borderRadius: '8px !important',
                                                        backgroundColor: 'rgba(33, 43, 54, 0.08)',
                                                        '&:hover': {
                                                            backgroundColor: (theme) => alpha(theme.palette.primary.main, 0.08),
                                                            color: 'primary.main'
                                                        }
                                                    }}
                                                >
                                                    {t('course.error.course.empty.button')}
                                                </Button>
                                            </Box>
                                        </Box>
                                    </Grid>
                                }
                            </Box>
                        </Paper>
                    </Grid>
                    {course && ('_id' in course) && course?.sections?.length > 0 && mdUp && (
                        <Grid item md={2} sx={{ position: 'relative' }}>
                            <TOC
                                tab={toc}
                                config={course?.sections?.map(section => ({
                                    'label': section.title,
                                    'active': section === course?.sections[activeSection],
                                    'subsections': section.subsections
                                        .filter(subsection => subsection.content)
                                        .map(subsection => ({
                                            'id': subsection.id,
                                            'label': subsection.title
                                        }))
                                }))}
                                handleTOC={handleTOC}
                                disabled={user.courses.find((c) => c.id === _id)?.quiz?.some((q) => q.is_solving)}
                            />
                        </Grid>
                    )}
                </Grid>
                {user && course && ('_id' in course) && ((user._id === course?.user) || (user.courses.filter(c => c.id === course?._id).length > 0)) &&
                    <Stack direction='row' alignItems='center' justifyContent={mdUp ? 'flex-end' : 'center'} spacing={1}
                        sx={{
                            position: 'fixed',
                            bottom: 0,
                            right: 0,
                            mb: {
                                md: '40px',
                                xs: '16px'
                            },
                            mr: {
                                md: '40px'
                            },
                        }}
                    >
                        <Button
                            id='mISLec_course_chat_button'
                            variant=''
                            onClick={(event) => setChat(event.currentTarget)}
                            disabled={user.courses.find((c) => c.id === _id)?.quiz?.some((q) => q.is_solving)}
                            sx={{
                                borderRadius: '8px !important',
                                backgroundColor: {
                                    xs: '#E6EEF9',
                                    md: '#E7E8EA'
                                },
                                color: {
                                    xs: 'primary.main',
                                    md: 'inherit'
                                },
                                '&:hover': {
                                    color: 'primary.main',
                                    backgroundColor: '#E6EEF9'
                                }
                            }}
                        >
                            <Iconify icon='lucide:bot' sx={{ mr: { md: 1 }, mb: { md: 0.5 } }} width={24} />
                            <Stack sx={{ display: { xs: 'none', md: 'block' } }}>
                                {t('course.tabs.course.buttons.chat')}
                            </Stack>
                        </Button>
                    </Stack>
                }
                {course && ('_id' in course) &&
                    <Popover
                        open={!!chat}
                        anchorEl={chat}
                        onClose={() => setChat(null)}
                        anchorOrigin={{ vertical: 'top', horizontal: mdUp ? 'right' : 'center' }}
                        transformOrigin={{ vertical: 'bottom', horizontal: mdUp ? 'right' : 'center' }}
                        disableAutoFocus
                    >
                        <Paper className='mISLec_course_chat' sx={{ height: '75vh', width: mdUp ? '33vw' : '100%' }}>
                            <Chat user={user} updateUser={updateUser} course={course?._id} section={course?.sections[activeSection]?.id} findOutMorePrompt={findOutMorePrompt} />
                        </Paper>
                    </Popover>
                }
            </Container>
            {course && ('_id' in course) &&
                <>
                    <ConfirmationModal
                        type='remove'
                        title={t('course.modals.removeCourse.title')}
                        description={t('course.modals.removeCourse.description')}
                        confirmationText={t('course.modals.removeCourse.confirmation')}
                        confirm={remove}
                        open={removeCourseModal}
                        onClose={() => setRemoveCourseModal(false)}
                    />
                    <ConfirmationModal
                        type='remove'
                        title={t('course.modals.removeSection.title')}
                        description={t('course.modals.removeSection.description')}
                        confirmationText={t('course.modals.removeSection.confirmation')}
                        confirm={removeSection}
                        open={removeSectionModal}
                        onClose={() => setRemoveSectionModal(false)}
                    />
                    <ConfirmationModal
                        type='remove'
                        title={t('course.modals.removeSubsection.title')}
                        description={t('course.modals.removeSubsection.description')}
                        confirmationText={t('course.modals.removeSubsection.confirmation')}
                        confirm={removeSubsection}
                        open={removeSubsectionModal}
                        onClose={() => setRemoveSubsectionModal(false)}
                    />
                    <ConfirmationModal
                        type='remove'
                        title={t('course.modals.removeQuiz.title')}
                        description={t('course.modals.removeQuiz.description')}
                        confirmationText={t('course.modals.removeQuiz.confirmation')}
                        confirm={removeSubsection}
                        open={removeQuizModal}
                        onClose={() => setRemoveQuizModal(false)}
                    />
                    <ConfirmationModal
                        type='remove'
                        title={t('course.modals.removeMaterial.title')}
                        description={t('course.modals.removeMaterial.description')}
                        confirmationText={t('course.modals.removeMaterial.confirmation')}
                        confirm={removeMaterial}
                        open={removeMaterialModal}
                        onClose={() => { setRemoveMaterialModal(false); setRemoveMaterialId(null); }}
                    />
                    <ConfirmationModal
                        type='remove'
                        title={t('course.modals.revokeAccess.title')}
                        description={t('course.modals.revokeAccess.description')}
                        confirmationText={t('course.modals.revokeAccess.confirmation')}
                        confirm={revokeAccess}
                        open={revokeAccessModal}
                        onClose={() => { setRevokeAccessModal(false); setRevokeAccessId(null); }}
                    />
                    <ContentModal open={contentModal} setOpen={setContentModal} sections={course?.sections?.length} section={setSectionTypeModal} subsection={setSubsectionTypeModal} isExam={course?.sections[activeSection]?.subsections[0]?.quiz?.type === 'exam'} />
                    <SectionType open={sectionTypeModal} setOpen={setSectionTypeModal} section={setSectionModal} quiz={handleQuiz} />
                    <SubsectionType open={subsectionTypeModal} setOpen={setSubsectionTypeModal} subsection={setSubsectionModal} quiz={handleQuiz} />
                    <SectionModal user={user} updateUser={updateUser} courseId={course?._id} sectionId={course?.sections[activeSection]?.id} sectionTitle={sectionTitle} setSectionTitle={setSectionTitle} saveSection={saveSection} open={sectionModal} setOpen={setSectionModal} />
                    <SubsectionModal subsectionTitle={subsectionTitle} subsectionContent={subsectionContent} setSubsectionTitle={setSubsectionTitle} setSubsectionContent={setSubsectionContent} saveSubsection={saveSubsection} edit={editingSubsection} open={subsectionModal} setOpen={setSubsectionModal} />
                    <QuizModal user={user} updateUser={updateUser} quiz={quiz} setQuiz={setQuiz} final={finalExam} lng={course?.language} open={quizModal} setOpen={setQuizModal} courseId={course?._id} sections={course?.sections?.map(section => ({ id: section.id, title: section.title, type: section?.subsections[0]?.quiz?.type === 'exam' ? 'exam' : 'content' }))} sectionId={course?.sections[activeSection]?.id} saveQuiz={saveQuiz} setNotification={setOpen} setMessage={setMessage} setSeverity={setSeverity} />
                </>
            }
            <Notification open={open} message={message} severity={severity} onClose={() => setOpen(false)} />
        </>
    );
}

export default Course;
